import { reset, setSubmitFailed, setSubmitSucceeded, startSubmit, stopSubmit } from 'redux-form';
import { all, call, delay, getContext, put, race, select, takeEvery, takeLeading } from 'redux-saga/effects';
import i18n from '../../../i18n';
import type { IContactFormSubmissionData, IContactFormSubmissionRequest, ICustomerBase, IPortalAccount } from '../../api/domain';
import { ContactFormType } from '../../api/domain';
import type { ContactFormSubmissionService, SettingsApiService } from '../../api/services';
import { AuthenticationUrl, FORMS, NotificationType } from '../../enum';
import { gotoExternalLocation } from '../../utils/link-helpers';
import { scrollWindowToTop } from '../../utils/window';
import { addNotification, changeLocation, hideProgressIndicator, showProgressIndicator } from '../application/application-actions';
import { getSelectedCustomerBase } from '../application/application-selectors';
import { getOwnCustomerBase } from '../customer-base-api/customer-base-api-selectors';
import { logError } from '../logging-api/logging-api-actions';
import { getUserPortalAccount } from '../portal-account-api/portal-account-api-selectors';
import type { Action } from '../utils';
import { submitContactForm, submitContactFormFailure, submitContactFormTimeout } from './contact-form-actions';

const SUBMIT_TIMEOUT_DELAY_SECONDS = 10;

function* handleSubmitForm({ payload: formData }: Action<IContactFormSubmissionData>) {
    const api: ContactFormSubmissionService = yield getContext('contactFormService');
    const settingsApi: SettingsApiService = yield getContext('settingsApiService');

    yield put(startSubmit(FORMS.CONTACT_FORM));
    yield put(showProgressIndicator());

    try {
        const portalAccount: IPortalAccount = yield select(getUserPortalAccount);
        const ownCustomerBase: ICustomerBase | undefined = yield select(getOwnCustomerBase);
        const selectedCustomerBase: ICustomerBase | undefined = yield select(getSelectedCustomerBase);
        const customerBase = ownCustomerBase || selectedCustomerBase;

        const primaryCustomerId = portalAccount?.loginId;
        const contextCustomerId = customerBase?.externalId;
        const { 'x-tnt': userbank } = yield call([settingsApi, settingsApi.getContextHeaders]);
        const request: IContactFormSubmissionRequest = {
            ...formData,
            primaryCustomerId,
            contextCustomerId,
            creationTimestamp: new Date().toISOString(),
            sourceSystem: 'portal',
            userbank,
        };

        const { timeout } = yield race({
            res: call([api, api.submitContactForm], userbank, request),
            timeout: delay(SUBMIT_TIMEOUT_DELAY_SECONDS * 1000),
        });

        if (timeout) {
            yield put(submitContactFormTimeout());
        } else {
            yield scrollWindowToTop();

            // OBB-279: after submission of the registration form prospects will be redirected to IAM login
            if (formData.contactType === ContactFormType.EBANKING_REGISTRATION) {
                yield put(hideProgressIndicator());

                yield put(
                    addNotification({
                        text: i18n.t('ebanking-registration-form.submit-success'),
                        type: NotificationType.SUCCESS,
                    }),
                );

                yield delay(5000);

                yield put(reset(FORMS.CONTACT_FORM));
                yield put(setSubmitSucceeded(FORMS.CONTACT_FORM));

                gotoExternalLocation(AuthenticationUrl.LOGIN);
            } else {
                yield put(reset(FORMS.CONTACT_FORM));
                yield put(setSubmitSucceeded(FORMS.CONTACT_FORM));

                yield put(
                    addNotification({
                        text: i18n.t('contact-form.submit-success'),
                        type: NotificationType.SUCCESS,
                    }),
                );

                yield put(
                    changeLocation({
                        location: '/',
                    }),
                );
            }
        }
    } catch (error: unknown) {
        yield put(submitContactFormFailure());
        yield put(logError(`handleSubmitForm failed with: ${error}`));
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleSubmitContactFormFailure() {
    yield put(reset(FORMS.CONTACT_FORM));
    yield put(setSubmitFailed(FORMS.CONTACT_FORM));
    //eslint-disable-next-line @typescript-eslint/naming-convention
    yield put(stopSubmit(FORMS.CONTACT_FORM, { _error: 'contact-form.submit-error' }));
    yield put(
        addNotification({
            text: i18n.t('contact-form.submit-error'),
            type: NotificationType.ERROR,
        }),
    );
    yield scrollWindowToTop();
}

function* handleSubmitContactFormTimeout() {
    yield put(reset(FORMS.CONTACT_FORM));
    yield put(setSubmitFailed(FORMS.CONTACT_FORM));
    //eslint-disable-next-line @typescript-eslint/naming-convention
    yield put(stopSubmit(FORMS.CONTACT_FORM, { _error: 'contact-form.server-timeout' }));
    yield scrollWindowToTop();
}

export function* contactFormSaga() {
    yield all([takeLeading(submitContactForm, handleSubmitForm)]);
    yield all([takeEvery(submitContactFormFailure, handleSubmitContactFormFailure)]);
    yield all([takeEvery(submitContactFormTimeout, handleSubmitContactFormTimeout)]);
}
